/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import TableLoader from '../../common/TableLoader';
import TableSkeleton from '../../common/TableSkeleton';
import StatusChip from './InvoiceStatusChip';
import InvoiceMobileCard from './InvoiceMobileCard';
import NoDataRow from '../../common/NoDataRow';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { ReactComponent as PrinterIcon } from '../../../assets/SVG/printerblue.svg'; 
import { ReactComponent as SettleBillIcon } from '../../../assets/SVG/receipt-edit_2.svg';
import { ReactComponent as ReceiptIcon } from '../../../assets/SVG/receipt.svg';
import { ReactComponent as RefundIcon } from '../../../assets/SVG/money-recive.svg';
import { ReactComponent as DelinkIcon } from '../../../assets/SVG/delink.svg';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import ReceiptDialog from './ReceiptDialog';
import RefundDialog from './RefundDialog';
import RemarkPopover from '../common/RemarkPopover';
import { TABLE_HEADER_ROW_SX, renderTablePlaceholder } from '../../../utils/typographyStyles';
import {
  useInvoiceDetails,
  useBillingConfigOptions,
  useCreatePayment,
  buildCreatePaymentParams,
} from '../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';
import {
  CollectPaymentModal,
  PaymentDetails,
  PaymentFormValues,
  PaymentHistoryItem,
  buildPaymentApiPayload,
} from '../../../pages/Billing/collectPayment';
import type { PaymentApiPayload } from '../../../pages/Billing/collectPayment';
import { useClinic } from '../../ClinicProvider';
import useCurrency from '../../CurrencyProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { InvoiceReportGen } from '../../Reports/Invoice';
import { PharmacyInvoiceReportGen } from '../../Reports/PharmacyInvoice';
import { usePharmacyInvoiceDetails } from '../../../pages/Billing/Pharmacy/viewPharmacyInvoice.helper';
import { usePharmacyPaymentsQuery } from '../../../pages/Billing/Pharmacy/pharmacyReceiptHelper';
import { storePrevRoute } from '../../../utils/commonUtils';
import { formatDateDMY } from '../../../utils/dateUtils';
import { hasDisplayValue } from '../../../utils/tableDisplayUtils';
// import { getStickyHeaderCellSx, getStickyBodyCellSx, STICKY_COLUMN_PRESETS } from '../../../utils/stickyColumnUtils';
import { useAlert } from '../../alertProvider';
import {
  usePackageAdvanceCollection,
  shouldShowCollectAdvanceButton,
  buildPaymentDetails,
  buildPackagePaymentItems,
  INVOICE_PAYMENT_AGAINST_TYPE,
} from './PackageAdvanceCollection';
import type { AdvanceConsumptionMetadata, PaymentModeEntry } from './PackageAdvanceCollection';
import InsufficientAdvancePopup from '../../../pages/Billing/Invoice/newInvoice/InsufficientAdvancePopup';
import CollectPackageAdvanceModal from '../../../pages/Billing/Invoice/newInvoice/CollectPackageAdvanceModal';
import DelinkRecipientDialog from './DelinkRecipientDialog';
import DelinkLogPopover from './DelinkLogPopover';
import TransferLogModal from './TransferLogModal';
import SettleBillModal from './SettleBillModal';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import RecipientSelectionModal from '../../../pages/Billing/Invoice/newInvoice/RecipientSelectionModal';
import {
  fetchLinkedRecipients,
  markSettlementSource,
  assignToRecipient,
} from '../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';
import type { LinkedRecipient } from '../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';

/** --- Types & config --- */
export interface InvoiceData {
  date: string;
  patient_id: string | number;
  patientName: string;
  patientInfo: string;
  mrn?: string;
  status: string;
  billNo: string;
  clinic?: string | null;
  billType: string;
  provider: string;
  invoiceDueStatus?: string | null;
  totalAmt: string;
  concessionAmt: string;
  netAmt: string;
  paidAmt: string;
  balanceAmt: string;
  billRemark: string | null;
  patientId: string | number;
  appointment_id?: number | null;
  id?: number | null;
  clinic_id?: number | null; // Clinic ID from API response (normalized to number)
  advance_consumption_metadata?: AdvanceConsumptionMetadata[] | null;
  paymentSource?: 'donor' | 'recipient' | null;
  label?: string | null;
  labelMrnNo?: string | null;
  can_settle_invoice?: boolean;
  invoice_patient_type?: string | null;
  is_donor_tagged?: boolean;
  delink_log?: {
    id: number;
    action: string;
    recipient_patient_id: number | null;
    recipient_name: string | null;
    recipient_mrn: string | null;
    reason: string | null;
    created_on: string | null;
    created_by: string | null;
  } | null;
  payment_count?: number;
}

interface InvoiceTableProps {
  data: InvoiceData[];
  loading?: boolean;
  patientId?: number;
}

const getTableLabels = (tBilling: (key: string) => string) => ({
  noData: tBilling('invoice.table.no_data'),
  loading: tBilling('invoice.table.loading'),
  date: tBilling('invoice.table.date'),
  patientName: tBilling('invoice.table.patient_name'),
  status: tBilling('invoice.table.status'),
  billNo: tBilling('invoice.table.bill_no'),
  clinic: tBilling('invoice.table.clinic'),
  billType: tBilling('invoice.table.bill_type'),
  provider: tBilling('invoice.table.provider'),
  totalAmt: tBilling('invoice.table.total_amt'),
  concessionAmt: tBilling('invoice.table.concession_amt'),
  netAmt: tBilling('invoice.table.net_amt'),
  paidAmt: tBilling('invoice.table.paid_amt'),
  balanceAmt: tBilling('invoice.table.balance_amt'),
  billRemark: tBilling('invoice.table.bill_remark'),
  actions: tBilling('invoice.table.actions'),
});

const getActionLabels = (tBilling: (key: string) => string) => ({
  edit: tBilling('invoice.actions.edit'),
  view: tBilling('invoice.actions.view'),
  print: tBilling('invoice.actions.print'),
  more: tBilling('invoice.actions.more'),
  settle: tBilling('invoice.actions.settle'),
  receipt: tBilling('invoice.actions.receipt'),
  refund: tBilling('invoice.actions.refund'),
  openRemark: tBilling('invoice.actions.open_remark'),
  add: tBilling('invoice.actions.add'),
  collectAdvance: tBilling('invoice.actions.collect_advance') || 'Collect Advance Balance',
  delink: 'Delink',
  transferLog: 'Transfer Log',
});

/** --- Component --- */
const getHeaderCells = (currencySymbol: string, TABLE_LABELS: ReturnType<typeof getTableLabels>): Array<{
  key: string;
  label: string;
  minWidth?: number;
  align?: 'center' | 'left' | 'right';
  noWrap?: boolean;
}> => [
  { key: 'date', label: TABLE_LABELS.date, minWidth: 120, noWrap: true },
  {
    key: 'patientName',
    label: TABLE_LABELS.patientName,
    minWidth: 200,
    noWrap: true,
  },
  { key: 'status', label: TABLE_LABELS.status, noWrap: true },
  { key: 'billNo', label: TABLE_LABELS.billNo, noWrap: true },
  { key: 'clinic', label: TABLE_LABELS.clinic, noWrap: true, minWidth: 140 },
  { key: 'billType', label: TABLE_LABELS.billType, noWrap: true },
  { key: 'provider', label: TABLE_LABELS.provider, noWrap: true },
  { key: 'totalAmt', label: `${TABLE_LABELS.totalAmt} (${currencySymbol})`, align: 'right', noWrap: true },
  {
    key: 'concessionAmt',
    label: `${TABLE_LABELS.concessionAmt} (${currencySymbol})`,
    align: 'right',
    noWrap: true,
  },
  { key: 'netAmt', label: `${TABLE_LABELS.netAmt} (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'paidAmt', label: `${TABLE_LABELS.paidAmt} (${currencySymbol})`, align: 'right', noWrap: true },
  {
    key: 'balanceAmt',
    label: `${TABLE_LABELS.balanceAmt} (${currencySymbol})`,
    align: 'right',
    noWrap: true,
  },
  { key: 'billRemark', label: TABLE_LABELS.billRemark, noWrap: true },
  {
    key: 'actions',
    label: TABLE_LABELS.actions,
    align: 'center',
    minWidth: 92,
    noWrap: true,
  },
];

const commonCellSx = {
  verticalAlign: 'middle' as const,
  px: 2.5,
  py: 1.5,
};

/**
 * Maps an InvoiceData table row to an InvoiceRowData object consumed by
 * the PackageAdvanceCollection hook.  Avoids repeating this mapping inline.
 */
const toInvoiceRowData = (row: InvoiceData) => ({
  id: row.id,
  patientId: row.patientId ?? row.patient_id,
  patient_id: row.patient_id,
  patientName: row.patientName || 'Patient',
  mrn: row.mrn,
  balanceAmt: row.balanceAmt,
  billType: row.billType,
  appointment_id: row.appointment_id,
  advance_consumption_metadata: row.advance_consumption_metadata,
});

function InvoiceTable({
  data,
  loading = false,
  patientId,
}: InvoiceTableProps) {
  const { t : tBilling } = useTranslation('billing_config');
  const TABLE_LABELS = useMemo(() => getTableLabels(tBilling), [tBilling]);
  const ACTION_LABELS = useMemo(() => getActionLabels(tBilling), [tBilling]);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { ClinicId } = useClinic();
  const { currency, currencySymbol } = useCurrency();
  const createPaymentMutation = useCreatePayment();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { addAlert } = useAlert();
  // Generate header cells with currency symbol
  const headerCells = useMemo(() => getHeaderCells(currencySymbol, TABLE_LABELS), [currencySymbol, TABLE_LABELS]);

  // Unique ids for menu and dialog items for test automation.
  const getRowId = (row: InvoiceData, idx: number) =>
    `invoice-table-row-${row.id ?? row.appointment_id ?? row.patient_id ?? idx}-${idx}`;
  const getTestId = (row: InvoiceData, idx: number) =>
    `invoice-table-row-${row.id ?? row.appointment_id ?? row.patient_id ?? idx}-${idx}`;

  // Menu state for row actions
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<InvoiceData | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Receipt dialog state
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptBillNo, setReceiptBillNo] = useState<string | null>(null);
  const [receiptInvoiceId, setReceiptInvoiceId] = useState<number>(0);

  // Refund dialog state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundInvoiceId, setRefundInvoiceId] = useState<number | null>(null);
  const [refundPatientId, setRefundPatientId] = useState<string | number | null>(null);
  const [refundPatientName, setRefundPatientName] = useState<string>('');

  // Collect Payment modal
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] =
    useState<PaymentDetails | null>(null);
  const [settlePatientId, setSettlePatientId] = useState<string | number | null>(null);
  const [settleInvoiceId, setSettleInvoiceId] = useState<number | null>(null);
  const [consumeFromPackageForModal, setConsumeFromPackageForModal] = useState<number>(0);
  // Metadata from the invoice row — used to build package advance payment items
  const [settleMetadata, setSettleMetadata] = useState<AdvanceConsumptionMetadata[] | null>(null);
  // Package balance from validate API — shown in banner info popover
  const [packageBalanceForModal, setPackageBalanceForModal] = useState<number>(0);
  // Package name from validate API — shown in banner
  const [packageNameForModal, setPackageNameForModal] = useState<string>('');

  /**
   * Resets all settle/payment modal state to initial values.
   * Centralised to avoid repeating the same 8-line reset in multiple callbacks.
   */
  const resetSettleModalState = useCallback(() => {
    setCollectPaymentOpen(false);
    setPaymentDetailsForModal(null);
    setSettlePatientId(null);
    setSettleInvoiceId(null);
    setConsumeFromPackageForModal(0);
    setSettleMetadata(null);
    setPackageBalanceForModal(0);
    setPackageNameForModal('');
  }, []);

  // Package Advance Collection hook (placed after state declarations it depends on)
  const {
    state: pkgAdvState,
    actions: pkgAdvActions,
  } = usePackageAdvanceCollection({
    patientId: patientId ?? null,
    clinicId: ClinicId ? Number(ClinicId) : null,
    currency: typeof currency === 'string' ? currency : 'INR',
    onOpenRegularPayment: (details, consumeFromPackage, packageBalance, packageName, metadata) => {
      setPaymentDetailsForModal(details);
      setConsumeFromPackageForModal(consumeFromPackage ?? 0);
      setPackageBalanceForModal(packageBalance ?? 0);
      setPackageNameForModal(packageName ?? '');
      // Use metadata passed directly from the hook (avoids stale-closure reading pkgAdvState)
      setSettleMetadata(metadata ?? null);
      setCollectPaymentOpen(true);
    },
    onAlert: (message, severity) => addAlert(message, severity),
    onAdvancePaymentSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onSettleAfterAdvance: (row, paymentDetails, consumeFromPackage, packageBalance, packageName) => {
      // Auto-open the regular settle modal for the same invoice.
      //
      // The user just COLLECTED advance (credited the package balance in step 1).
      // In this settle step we still need to CONSUME from that balance, so we
      // forward advance_consumption_metadata so handlePaymentSave can build the
      // advance-deduction payment items and include them in the API payload.
      setSettlePatientId(row.patientId ?? row.patient_id ?? null);
      setSettleInvoiceId(row.id ?? null);
      setPaymentDetailsForModal(paymentDetails);
      setConsumeFromPackageForModal(consumeFromPackage ?? 0);
      setPackageBalanceForModal(packageBalance ?? 0);
      setPackageNameForModal(packageName ?? '');
      // Keep metadata so the advance-consumption payment items are built correctly
      setSettleMetadata(row.advance_consumption_metadata ?? null);
      setCollectPaymentOpen(true);
    },
  });

  // Invoice Report dialog
  const [invoiceReportOpen, setInvoiceReportOpen] = useState(false);
  const [invoiceReportId, setInvoiceReportId] = useState<number | null>(null);

  // Pharmacy Report dialog
  const [pharmacyReportOpen, setPharmacyReportOpen] = useState(false);
  const [pharmacyReportId, setPharmacyReportId] = useState<number | null>(null);

  // Data fetches 
  const { data: billingConfigData } = useBillingConfigOptions(settlePatientId ?? undefined);
  const paymentModes = useMemo(() => {
    if (!billingConfigData) return [];
    // defensive
    const config: { modeofpayment?: any[] } = billingConfigData as any;
    const modes: any[] = config?.modeofpayment ?? [];
    return Array.isArray(modes)
      ? modes.map((item) => ({
        id: item.id,
        name: item.name,
        code: String(item.code),
      }))
      : [];
  }, [billingConfigData]);

  const upiProviders = useMemo(() => {
    if (!billingConfigData) return undefined;
    const config: { UPI_PROVIDER?: any[] } = billingConfigData as any;
    const providers: any[] = config?.UPI_PROVIDER ?? [];
    if (!Array.isArray(providers) || providers.length === 0) return undefined;
    return providers.map((item) => ({
      id: item.id,
      name: item.name,
      code: String(item.code),
    }));
  }, [billingConfigData]);

  useInvoiceDetails(settleInvoiceId ?? undefined);
  const {
    data: invoiceReportDetails,
    isLoading: invoiceReportLoading,
    error: invoiceReportError,
  } = useInvoiceDetails(invoiceReportId ?? undefined);

  // Pharmacy report data fetch
  const {
    data: pharmacyReportDetails,
    isLoading: pharmacyReportLoading,
    error: pharmacyReportError,
  } = usePharmacyInvoiceDetails(pharmacyReportId ?? undefined);

  // Fetch payment transactions for pharmacy report
  const { data: pharmacyPaymentsData } = usePharmacyPaymentsQuery({
    invoiceId: pharmacyReportId ?? 0,
    enabled: pharmacyReportOpen && (pharmacyReportId ?? 0) > 0,
  });

  // Accessibility & automation overlays/popovers
  const [remarkAnchorEl, setRemarkAnchorEl] = useState<null | HTMLElement>(null);
  const [remarkText, setRemarkText] = useState<string>('');

  // Label (Donor/Recipient) popover
  const [labelAnchorEl, setLabelAnchorEl] = useState<null | HTMLElement>(null);
  const [labelPopoverMrn, setLabelPopoverMrn] = useState<string>('');

  // Delink log popover
  const [delinkLogAnchorEl, setDelinkLogAnchorEl] = useState<null | HTMLElement>(null);
  const [delinkLogData, setDelinkLogData] = useState<InvoiceData['delink_log']>(null);

  // Show/hide patient column for automation
  const showPatientColumn = !patientId;
  const visibleHeaderCells = useMemo(
    () => headerCells.filter((h) => showPatientColumn || h.key !== 'patientName'),
    [showPatientColumn, headerCells]
  );

  /** Row menu handlers */
  const handleMoreClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: InvoiceData) => {
      setMenuAnchorEl(event.currentTarget);
      setMenuRow(row);
    },
    []
  );
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  }, []);

  // Settle Bill modal state (donor chooser: Continue with Donor / Recipient)
  const [settleBillModalOpen, setSettleBillModalOpen] = useState(false);
  const [settleBillRow, setSettleBillRow] = useState<InvoiceData | null>(null);

  // Recipient selection modal state
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipients, setRecipients] = useState<LinkedRecipient[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  /** Core settle logic — opens the payment modal for a given row. */
  const openPaymentFlowForRow = useCallback(async (row: InvoiceData) => {
    setSettlePatientId(row.patientId ?? row.patient_id);
    setSettleInvoiceId(row.id ?? null);

    const handled = await pkgAdvActions.handleSettleBillWithPackageCheck(
      toInvoiceRowData(row),
    );
    if (handled) return;

    setSettleInvoiceId(row.id ?? null);
    setConsumeFromPackageForModal(0);
    setSettleMetadata(null);
    setPackageBalanceForModal(0);
    setPackageNameForModal('');
    const effectiveCurrency = typeof currency === 'string' ? currency : 'INR';
    setPaymentDetailsForModal(buildPaymentDetails(row as any, effectiveCurrency));
    setCollectPaymentOpen(true);
  }, [currency, pkgAdvActions]);

  const handleSettleBill = useCallback(async () => {
    if (!menuRow) return;
    handleMenuClose();

    // Pre-set settlePatientId so useBillingConfigOptions fires early
    setSettlePatientId(menuRow.patientId ?? menuRow.patient_id);
    setSettleInvoiceId(menuRow.id ?? null);

    // If the invoice is a donor invoice and not yet tagged to a recipient, show the Settle Bill chooser first
    if (menuRow.invoice_patient_type === 'donor' && menuRow.is_donor_tagged === false) {
      setSettleBillRow(menuRow);
      setSettleBillModalOpen(true);
      return;
    }

    // Non-donor: open payment flow directly
    await openPaymentFlowForRow(menuRow);
  }, [menuRow, handleMenuClose, openPaymentFlowForRow]);

  /** "Continue with Donor" — donor pays directly */
  const handleSettleContinueWithDonor = useCallback(async () => {
    if (!settleBillRow) return;
    setSettleBillModalOpen(false);
    await openPaymentFlowForRow(settleBillRow);
    setSettleBillRow(null);
  }, [settleBillRow, openPaymentFlowForRow]);

  /** "Continue with Recipient" — fetch linked recipients and show selection modal */
  const handleSettleContinueWithRecipient = useCallback(async () => {
    if (!settleBillRow) return;
    setSettleBillModalOpen(false);

    const donorId = settleBillRow.patientId ?? settleBillRow.patient_id;
    if (!donorId) return;

    setRecipientsLoading(true);
    setRecipientModalOpen(true);
    try {
      const data = await fetchLinkedRecipients(Number(donorId));
      const recipientList = data?.recipients ?? [];
      setRecipients(recipientList);
      setSelectedRecipientId(recipientList.length === 1 ? recipientList[0].id : null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch recipients:', err);
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  }, [settleBillRow]);

  /** Handle recipient selection proceed — mark settlement source + assign to recipient */
  const handleRecipientProceed = useCallback(async (recipientId?: number) => {
    if (!settleBillRow) return;
    const invoiceId = settleBillRow.id;
    if (!invoiceId) return;

    try {
      await markSettlementSource(invoiceId, 'recipient');
      if (recipientId) {
        await assignToRecipient(invoiceId, recipientId);
        addAlert('Invoice assigned to recipient successfully', 'success');
      } else {
        addAlert('Invoice saved as unsettled', 'success');
      }
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to assign recipient:', err);
      addAlert('Failed to process recipient invoice. Please try again.', 'error');
    } finally {
      setRecipientModalOpen(false);
      setRecipients([]);
      setSelectedRecipientId(null);
      setSettleBillRow(null);
    }
  }, [settleBillRow, addAlert, queryClient]);

  const handleRecipientModalClose = useCallback(() => {
    setRecipientModalOpen(false);
    setRecipients([]);
    setSelectedRecipientId(null);
    setSettleBillRow(null);
  }, []);

  const handleSettleBillModalClose = useCallback(() => {
    setSettleBillModalOpen(false);
    setSettleBillRow(null);
  }, []);

  // Payment save
  const handlePaymentSave = useCallback(
    async (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
      if (!settleInvoiceId || !settlePatientId || !ClinicId) return;
      const invoiceIdNum = Number(settleInvoiceId);
      const patientIdNum = Number(settlePatientId);
      const clinicIdNum = Number(ClinicId);

      // Build package-advance payment items from metadata (DRY helper)
      const packagePayments = consumeFromPackageForModal > 0
        ? buildPackagePaymentItems(settleMetadata, paymentModes as PaymentModeEntry[])
        : [];
      const combinedHistory = [...packagePayments, ...history];

      let payload: PaymentApiPayload;
      if (data.paymentMode === 'credit') {
        payload = {
          invoice_id: invoiceIdNum,
          patient_id: patientIdNum,
          clinic_id: clinicIdNum,
          payment_against_type: INVOICE_PAYMENT_AGAINST_TYPE,
          remarks: '',
          payment_details: [],
        };
      } else {
        payload = buildPaymentApiPayload(
          combinedHistory,
          invoiceIdNum,
          patientIdNum,
          clinicIdNum,
          currency,
          INVOICE_PAYMENT_AGAINST_TYPE,
          ''
        );
      }
      try {
        await createPaymentMutation.mutateAsync(
          buildCreatePaymentParams(payload as any)
        );
        await queryClient.invalidateQueries({ queryKey: ['invoices'] });
        resetSettleModalState();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create payment from invoice table', error);
        throw error;
      }
    },
    [
      settleInvoiceId,
      settlePatientId,
      ClinicId,
      createPaymentMutation,
      queryClient,
      currency,
      consumeFromPackageForModal,
      settleMetadata,
      paymentModes,
      resetSettleModalState,
    ]
  );

  /** Close the collect-payment modal and reset all related state. */
  const handleCollectPaymentClose = useCallback(() => {
    resetSettleModalState();
  }, [resetSettleModalState]);

  // Invoice report
  const handlePrintInvoice = useCallback((invoiceId: number | null, billType?: string) => {
    if (!invoiceId) return;
    const billTypeLower = billType?.toLowerCase();
    const isPharmacy = billTypeLower === 'ph' || billTypeLower === 'pharmacy';
    if (isPharmacy) {
      setPharmacyReportId(invoiceId);
      setPharmacyReportOpen(true);
    } else {
      setInvoiceReportId(invoiceId);
      setInvoiceReportOpen(true);
    }
  }, []);
  const handleInvoiceReportClose = useCallback(() => {
    setInvoiceReportOpen(false);
    setInvoiceReportId(null);
  }, []);
  const handlePharmacyReportClose = useCallback(() => {
    setPharmacyReportOpen(false);
    setPharmacyReportId(null);
  }, []);

  // Receipt
  const handleReceipt = useCallback(() => {
    if (!menuRow) return;
    setReceiptInvoiceId(menuRow.id ?? 0);
    setReceiptBillNo(menuRow.billNo);
    setReceiptOpen(true);
    handleMenuClose();
  }, [menuRow, handleMenuClose]);
  const handleReceiptClose = useCallback(() => {
    setReceiptOpen(false);
    setReceiptBillNo(null);
    setReceiptInvoiceId(0);
  }, []);

  // Refund
  const handleRefund = useCallback(() => {
    if (!menuRow) return;
    const invoiceId = menuRow.id;
    if (invoiceId) setRefundInvoiceId(invoiceId);
    setRefundPatientId(menuRow.patientId ?? menuRow.patient_id);
    setRefundPatientName(menuRow.patientName || '');
    setRefundOpen(true);
    handleMenuClose();
  }, [menuRow, handleMenuClose]);
  const handleRefundClose = useCallback(() => {
    setRefundOpen(false);
    setRefundInvoiceId(null);
    setRefundPatientId(null);
    setRefundPatientName('');
  }, []);

  // Delink dialog state
  const [delinkOpen, setDelinkOpen] = useState(false);
  const [delinkInvoiceId, setDelinkInvoiceId] = useState<number | null>(null);

  const handleDelink = useCallback(() => {
    if (!menuRow) return;
    setDelinkInvoiceId(menuRow.id ?? null);
    setDelinkOpen(true);
    handleMenuClose();
  }, [menuRow, handleMenuClose]);

  const handleDelinkClose = useCallback(() => {
    setDelinkOpen(false);
    setDelinkInvoiceId(null);
  }, []);

  // Transfer log modal state
  const [transferLogOpen, setTransferLogOpen] = useState(false);
  const [transferLogInvoiceId, setTransferLogInvoiceId] = useState<number | null>(null);

  const handleTransferLog = useCallback(() => {
    if (!menuRow) return;
    setTransferLogInvoiceId(menuRow.id ?? null);
    setTransferLogOpen(true);
    handleMenuClose();
  }, [menuRow, handleMenuClose]);

  const handleTransferLogClose = useCallback(() => {
    setTransferLogOpen(false);
    setTransferLogInvoiceId(null);
  }, []);

  // Remark popover
  const handleOpenRemark = useCallback(
    (event: React.MouseEvent<HTMLElement>, text: string) => {
      setRemarkAnchorEl(event.currentTarget);
      setRemarkText(text);
    },
    []
  );
  const handleCloseRemark = useCallback(() => {
    setRemarkAnchorEl(null);
    setRemarkText('');
  }, []);

  // Delink log popover handlers
  const handleOpenDelinkLog = useCallback(
    (event: React.MouseEvent<HTMLElement>, log: InvoiceData['delink_log']) => {
      event.stopPropagation();
      setDelinkLogAnchorEl(event.currentTarget);
      setDelinkLogData(log);
    },
    []
  );
  const handleCloseDelinkLog = useCallback(() => {
    setDelinkLogAnchorEl(null);
    setDelinkLogData(null);
  }, []);

  // Label popover handlers
  const handleOpenLabelPopover = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: InvoiceData) => {
      event.stopPropagation();
      setLabelPopoverMrn(row.labelMrnNo ?? '');
      setLabelAnchorEl(event.currentTarget);
    },
    []
  );
  const handleCloseLabelPopover = useCallback(() => {
    setLabelAnchorEl(null);
    setLabelPopoverMrn('');
  }, []);
  const [mrnCopied, setMrnCopied] = useState(false);
  const handleCopyMrn = useCallback(async () => {
    if (!labelPopoverMrn) {
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(labelPopoverMrn);
      } else {
        throw new Error('Clipboard API not available');
      }
      setMrnCopied(true);
      setTimeout(() => setMrnCopied(false), 1500);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy MRN to clipboard', error);
    }
  }, [labelPopoverMrn]);

  // Table row navigation
  const handleRowClick = useCallback(
    (row: InvoiceData) => {
      if (!row.patientId && !row.patient_id) return;
      if(!patientId){
        storePrevRoute(location.pathname, location.search);
      }
      const status = row.status?.toLowerCase();
      const isDraft = status === 'draft';
      const isUnbilled = status === 'unbilled';
      const isDispensed = status === 'dispensed';
      const billTypeLower = row.billType?.toLowerCase();
      const isPharmacy = billTypeLower === 'ph' || billTypeLower === 'pharmacy';
      const isPackage = billTypeLower === 'pk' || billTypeLower === 'package'; 
      const patientIdForRoute = row.patient_id ?? row.patientId;
      if (isUnbilled || isDraft || isDispensed) {
        if (isDraft || isDispensed) {
          if (isPharmacy) {
            navigate(`/admin/billing/pharmacy/new/${patientIdForRoute}`, {
              state: { invoice_id: row.id ?? null, bill_type: row.billType ?? null },
            });
          } else {
            navigate(`/admin/billing/invoice/new/${patientIdForRoute}`, {
              state: { invoice_id: row.id ?? null, bill_type: row.billType ?? null },
            });
          }
        } else {
          // Unbilled status
          if (isPharmacy) {
            navigate(`/admin/billing/pharmacy/new/${patientIdForRoute}`, {
              state: { appointment_id: row.appointment_id ?? null, bill_type: row.billType ?? null },
            });
          } else if (isPackage) {
            // Package types should pass invoice_id
            navigate(`/admin/billing/invoice/new/${patientIdForRoute}`, {
              state: { invoice_id: row.id ?? null, bill_type: row.billType ?? null },
            });
          } else {
            navigate(`/admin/billing/invoice/new/${patientIdForRoute}`, {
              state: { appointment_id: row.appointment_id ?? null, bill_type: row.billType ?? null },
            });
          }
        }
      } else {
        // Billed or other statuses - view mode
        if (isPharmacy) {
          navigate(`/admin/billing/pharmacy/view/${patientIdForRoute}`, {
            state: { invoice_id: row.id ?? null },
          });
        } else {
          navigate(`/admin/billing/invoice/view/${patientIdForRoute}`, {
            state: { invoice_id: row.id ?? null },
          });
        }
      }
    },
    [location.pathname, location.search, navigate, patientId]
  );

  // --- Render ---

  if (isMobile) {
    return (
      <Box sx={{ width: '100%', p: 1, minHeight: 200, position: 'relative' }}>
        {loading ? (
          <TableLoader data-testid="invoice-loader" />
        ) : data && data.length > 0 ? (
          <Grid2 container spacing={2} sx={{ flexGrow: 1 }}>
            {data.map((row, idx) => (
              <Grid2
                key={getRowId(row, idx)}
                size={12}
                data-testid={getTestId(row, idx)}
                id={getRowId(row, idx)}
              >
                <InvoiceMobileCard
                  row={row}
                />
              </Grid2>
            ))}
          </Grid2>
        ) : (
          <NoDataRow
            message={TABLE_LABELS.noData}
            colSpan={4}
            data-testid="invoice-nodata"
          />
        )}
      </Box>
    );
  }

  return (
    <Box component="section" sx={{ width: '100%', overflowX: 'auto', position: 'relative' }} aria-label="Invoice data table section">
      <TableContainer
        component={Paper}
        sx={{
          minWidth: 360,
          maxHeight: 'calc(100vh - 360px)',
          overflowY: 'auto',
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="Invoice list table with billing details"
          role="table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& th, & td': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            // '& th:last-of-type, & td:last-of-type': {
            //   pr: 4,
            // },
          }}
        >
          <TableHead role="rowgroup">
            <TableRow
              sx={TABLE_HEADER_ROW_SX}
              role="row"
              id="invoice-table-header-row"
              data-testid="invoice-table-header-row"
            >
              {visibleHeaderCells.map((h, index) => (
                <TableCell
                  key={h.key}
                  align={h.align}
                  sx={{
                    ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                    ...(h.noWrap ? { whiteSpace: 'nowrap' } : {}),
                    ...(h.key === 'actions'
                      ? {
                        px: 2,
                        pr: 4,
                        minWidth: 85,
                        // ...getStickyHeaderCellSx(STICKY_COLUMN_PRESETS.actionsRight),
                      }
                      : {
                        px: 2.5,
                      }),
                    ...(index === 0
                      ? {
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      }
                      : {}),
                    ...(index === visibleHeaderCells.length - 1
                      ? {
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                      }
                      : {}),
                  }}
                  scope="col"
                  aria-colindex={index + 1}
                  id={`invoice-header-cell-${h.key}`}
                  data-testid={`invoice-header-cell-${h.key}`}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody role="rowgroup">
            {loading ? (
              <TableSkeleton
                columnWidths={[
                  120, 240, 120, 140, 120, 140, 160, 120, 120, 120, 120, 120, 200, 140,
                ]}
                rows={10}
                data-testid="invoice-table-skeleton"
              />
            ) : data && data.length > 0 ? (
              data.map((row, idx) => {
                const status = row.status?.toLowerCase();
                const rowId = getRowId(row, idx);
                const testId = getTestId(row, idx);

                return (
                  <TableRow
                    key={rowId}
                    id={rowId}
                    data-testid={testId}
                    hover
                    sx={{
                      height: 60,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleRowClick(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                    tabIndex={0}
                    role="row"
                    aria-rowindex={idx + 2}
                  >
                    <TableCell
                      sx={{
                        ...commonCellSx,
                        minWidth: 120,
                        whiteSpace: 'nowrap',
                      }}
                      id={`${rowId}-date`}
                      data-testid={`${testId}-date`}
                    >
                      <Box>
                        <Box>
                          {hasDisplayValue(row.date) ? formatDateDMY(row.date) : renderTablePlaceholder()}
                        </Box>
                        {row.status?.toLowerCase() === 'draft' && hasDisplayValue(row.invoiceDueStatus) && (
                          <Box sx={{ mt: 0.5 }}>
                            <StatusChip
                              status={row.invoiceDueStatus ?? ''}
                              height={20}
                              sx={{ fontSize: 10, px: 0.5, fontWeight: 600 }}
                              data-testid={`${testId}-due-status-chip`}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    {showPatientColumn && (
                      <TableCell
                        sx={{
                          ...commonCellSx,
                          minWidth: 240,
                        }}
                        id={`${rowId}-patientName`}
                        data-testid={`${testId}-patientName`}
                      >
                        <Box>
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            {hasDisplayValue(row.patientName) ? row.patientName : '-'}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: '#888',
                              fontSize: 13,
                              display: 'block',
                              mt: 0.25,
                            }}
                          >
                            {hasDisplayValue(row.patientInfo) ? row.patientInfo : '-'}
                          </Box>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell
                      sx={commonCellSx}
                      id={`${rowId}-status`}
                      data-testid={`${testId}-status`}
                    >
                      <StatusChip
                        status={row.status}
                        width={100}
                        height={24}
                        sx={{ fontSize: 12, mt: 0.5 }}
                        data-testid={`${testId}-status-chip`}
                      />
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={commonCellSx}
                      id={`${rowId}-billNo`}
                      data-testid={`${testId}-billNo`}
                    >
                      <Box component="span" sx={{ display: 'inline-block', textAlign: 'left' }}>
                        {hasDisplayValue(row.billNo) ? row.billNo : '-'}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...commonCellSx,
                        whiteSpace: 'nowrap',
                      }}
                      id={`${rowId}-clinic`}
                      data-testid={`${testId}-clinic`}
                    >
                      {hasDisplayValue(row.clinic) ? row.clinic : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      sx={commonCellSx}
                      id={`${rowId}-billType`}
                      data-testid={`${testId}-billType`}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        <span>{hasDisplayValue(row.billType) ? row.billType : renderTablePlaceholder()}</span>
                        {row.label && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <Box
                              component="span"
                              onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); handleOpenLabelPopover(e, row); }}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                px: 1,
                                height: 24,
                                borderRadius: '16px',
                                fontFamily: 'Nunito',
                                fontSize: { xs: '11px', sm: '12px' },
                                fontWeight: 700,
                                lineHeight: '13px',
                                letterSpacing: '0.04em',
                                whiteSpace: 'nowrap',
                                border: '1.5px solid',
                                ...(row.label === 'donor'
                                  ? { color: '#47B35F', borderColor: '#47B35F', backgroundColor: '#47B35F1A' }
                                  : { color: '#C3B21E', borderColor: '#C3B21E', backgroundColor: '#C3B21E1A' }),
                              }}
                            >
                              {row.label === 'donor' ? 'Donor' : 'Recipient'}
                            </Box>
                            <InfoIcon
                              onClick={(e: React.MouseEvent<SVGSVGElement>) => handleOpenLabelPopover(e as unknown as React.MouseEvent<HTMLElement>, row)}
                              sx={{
                                fontSize: 22,
                                cursor: 'pointer',
                                color: '#9E9E9E',
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...commonCellSx,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 160,
                      }}
                      id={`${rowId}-provider`}
                      data-testid={`${testId}-provider`}
                    >
                      {hasDisplayValue(row.provider) ? row.provider : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={commonCellSx}
                      id={`${rowId}-totalAmt`}
                      data-testid={`${testId}-totalAmt`}
                    >
                      {hasDisplayValue(row.totalAmt) ? row.totalAmt : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={commonCellSx}
                      id={`${rowId}-concessionAmt`}
                      data-testid={`${testId}-concessionAmt`}
                    >
                      {hasDisplayValue(row.concessionAmt) ? row.concessionAmt : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={commonCellSx}
                      id={`${rowId}-netAmt`}
                      data-testid={`${testId}-netAmt`}
                    >
                      {hasDisplayValue(row.netAmt) ? row.netAmt : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={commonCellSx}
                      id={`${rowId}-paidAmt`}
                      data-testid={`${testId}-paidAmt`}
                    >
                      {hasDisplayValue(row.paidAmt) ? row.paidAmt : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={commonCellSx}
                      id={`${rowId}-balanceAmt`}
                      data-testid={`${testId}-balanceAmt`}
                    >
                      {hasDisplayValue(row.balanceAmt) ? row.balanceAmt : renderTablePlaceholder()}
                    </TableCell>
                    <TableCell
                      sx={commonCellSx}
                      id={`${rowId}-billRemark`}
                      data-testid={`${testId}-billRemark`}
                    >
                      {row.billRemark && row.billRemark.trim().length > 0 &&
                      row.billRemark.trim() !== '-' ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              maxWidth: 260,
                            }}
                          >
                            <Tooltip title={ACTION_LABELS.openRemark} arrow>
                              <IconButton
                                size="small"
                                sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                                aria-label={ACTION_LABELS.openRemark}
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenRemark(e, row.billRemark || '');
                                }}
                                id={`${rowId}-remark-icon`}
                                data-testid={`${testId}-remark-icon`}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 200,
                              }}
                              id={`${rowId}-remark-text`}
                              data-testid={`${testId}-remark-text`}
                            >
                              {row.billRemark}
                            </Typography>
                          </Box>
                        ) : (
                          renderTablePlaceholder()
                        )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...commonCellSx,
                        px: 1,
                        pr: 4,
                        // ...getStickyBodyCellSx(STICKY_COLUMN_PRESETS.actionsRight),
                      }}
                      id={`${rowId}-actions`}
                      data-testid={`${testId}-actions`}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                          minWidth: 68,
                        }}
                      >
                        {row.delink_log ? (
                          <Tooltip title="Delink Info" arrow>
                            <IconButton
                              size="small"
                              aria-label="Delink Info"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDelinkLog(e, row.delink_log);
                              }}
                              id={`${rowId}-delink-log-btn`}
                              data-testid={`${testId}-delink-log-btn`}
                              sx={{ p: 0.5, color: '#5A8AEA' }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Box sx={{ width: 28 }} />
                        )}
                        {row.id ? (
                          <Tooltip title={ACTION_LABELS.print} arrow>
                            <IconButton
                              size="small"
                              aria-label={ACTION_LABELS.print}
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintInvoice(row.id ?? null, row.billType);
                              }}
                              id={`${rowId}-print-btn`}
                              data-testid={`${testId}-print-btn`}
                              sx={{ p: 0.5 }}
                            >
                              <PrinterIcon width={20} height={20} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Box sx={{ width: 32 }} />
                        )}
                        {(() => {
                          // Determine if menu icon should be shown based on available menu items
                          const statusLower = status?.toLowerCase();
                          const hasSettleBill = statusLower === 'unsettle' && (
                            row.can_settle_invoice !== false ||
                            (row.invoice_patient_type === 'donor' && !row.is_donor_tagged)
                          );
                          const hasCollectAdvance = statusLower === 'unsettle' && 
                            shouldShowCollectAdvanceButton(row.billType, Array.isArray(row.advance_consumption_metadata) && row.advance_consumption_metadata.length > 0);
                          const hasReceipt = (statusLower === 'billed' || statusLower === 'unsettle') && (row.payment_count ?? 0) > 1;
                          const hasRefund = statusLower === 'billed';
                          const hasDelink = row.label === 'donor' && statusLower !== 'billed';
                          const hasTransferLog = !!row.label;
                          
                          const showMenuIcon = hasSettleBill || hasCollectAdvance || hasReceipt || hasRefund || hasDelink || hasTransferLog;
                          
                          return showMenuIcon ? (
                            <Tooltip title={ACTION_LABELS.more} arrow>
                              <IconButton
                                size="small"
                                aria-label={ACTION_LABELS.more}
                                tabIndex={0}
                                aria-haspopup="menu"
                                aria-controls={menuOpen ? 'invoice-action-menu' : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoreClick(e, row);
                                }}
                                id={`${rowId}-more-btn`}
                                data-testid={`${testId}-more-btn`}
                                sx={{ p: 0.5 }}
                              >
                                <MoreVertOutlinedIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Box sx={{ width: 24 }} />
                          );
                        })()}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoDataRow
                message={TABLE_LABELS.noData}
                colSpan={visibleHeaderCells.length}
                data-testid="invoice-nodata"
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- menu: strictly slotProps for MUI modern best practices --- */}
      <Menu
        id="invoice-action-menu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // strict MUI v5+: puts any Paper and List props under slotProps
        slotProps={{
          paper: { sx: { minWidth: 180, borderRadius: 2 } },
          root: { id: 'invoice-menu-root' },
          list: {
            'aria-labelledby': 'actions',
            role: 'menu',
          }
        }}
        data-testid="invoice-menu-list"
      >
        {menuRow?.status?.toLowerCase() === 'unsettle' && (
          menuRow?.can_settle_invoice !== false ||
          (menuRow?.invoice_patient_type === 'donor' && !menuRow?.is_donor_tagged)
        ) && (
          <MenuItem
            onClick={handleSettleBill}
            id="settle-bill-action"
            data-testid="menu-settle-bill"
            aria-label={ACTION_LABELS.settle}
            role="menuitem"
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Tooltip title={ACTION_LABELS.settle} arrow>
                <SettleBillIcon />
              </Tooltip>
              <span>{ACTION_LABELS.settle}</span>
            </Box>
          </MenuItem>
        )}
        {menuRow?.status?.toLowerCase() === 'unsettle' &&
          shouldShowCollectAdvanceButton(
            menuRow?.billType,
            Array.isArray(menuRow?.advance_consumption_metadata) &&
              menuRow.advance_consumption_metadata.length > 0,
          ) && (
          <MenuItem
            onClick={() => {
              if (!menuRow) return;
              // Pre-set settlePatientId so paymentModes load before Step 2
              setSettlePatientId(menuRow.patientId ?? menuRow.patient_id);
              handleMenuClose();
              pkgAdvActions.handleCollectAdvanceFromMenu(toInvoiceRowData(menuRow));
            }}
            id="collect-advance-action"
            data-testid="menu-collect-advance"
            aria-label={ACTION_LABELS.collectAdvance}
            role="menuitem"
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Tooltip title={ACTION_LABELS.collectAdvance} arrow>
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: '#5A8AEA' }} />
              </Tooltip>
              <span>{ACTION_LABELS.collectAdvance}</span>
            </Box>
          </MenuItem>
        )}
        {(menuRow?.status?.toLowerCase() === 'billed' || menuRow?.status?.toLowerCase() === 'unsettle') && (menuRow?.payment_count ?? 0) > 1 && (
          <MenuItem
            onClick={handleReceipt}
            id="receipt-action"
            data-testid="menu-receipt"
            aria-label={ACTION_LABELS.receipt}
            role="menuitem"
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Tooltip title={ACTION_LABELS.receipt} arrow>
                <ReceiptIcon />
              </Tooltip>
              <span>{ACTION_LABELS.receipt}</span>
            </Box>
          </MenuItem>
        )}
        {menuRow?.status?.toLowerCase() === 'billed' &&  menuRow?.clinic_id && Number(menuRow.clinic_id) === Number(ClinicId) && (menuRow?.billType?.toLowerCase() !== 'package closer') && menuRow?.billType?.toLowerCase() !== 'ph' && menuRow?.billType?.toLowerCase() !== 'pharmacy' && (
          <MenuItem
            onClick={handleRefund}
            id="refund-action"
            data-testid="menu-refund"
            aria-label={ACTION_LABELS.refund}
            role="menuitem"
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Tooltip title={ACTION_LABELS.refund} arrow>
                <RefundIcon />
              </Tooltip>
              <span>{ACTION_LABELS.refund}</span>
            </Box>
          </MenuItem>
        )}
        {menuRow?.label === 'donor' && menuRow?.status?.toLowerCase() !== 'billed' && (
          <MenuItem
            onClick={handleDelink}
            id="delink-action"
            data-testid="menu-delink"
            aria-label={ACTION_LABELS.delink}
            role="menuitem"
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Tooltip title={ACTION_LABELS.delink} arrow>
                <DelinkIcon />
              </Tooltip>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {ACTION_LABELS.delink}
              </Box>
            </Box>
          </MenuItem>
        )}
        {menuRow?.label && (
          <MenuItem
            onClick={handleTransferLog}
            id="transfer-log-action"
            data-testid="menu-transfer-log"
            aria-label={ACTION_LABELS.transferLog}
            role="menuitem"
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Tooltip title={ACTION_LABELS.transferLog} arrow>
                <SwapHorizRoundedIcon sx={{ fontSize: 20 }} />
              </Tooltip>
              <span>{ACTION_LABELS.transferLog}</span>
            </Box>
          </MenuItem>
        )}
      </Menu>
      <RemarkPopover
        anchorEl={remarkAnchorEl}
        open={Boolean(remarkAnchorEl)}
        content={remarkText}
        onClose={handleCloseRemark}
        data-testid="invoice-remark-popover"
      />
      <Popover
        anchorEl={labelAnchorEl}
        open={Boolean(labelAnchorEl)}
        onClose={handleCloseLabelPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: 3, p: 0 } } }}
        data-testid="invoice-label-popover"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            MRN No. :{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {labelPopoverMrn || '-'}
            </Box>
          </Typography>
          <Tooltip title={mrnCopied ? 'Copied!' : 'Copy MRN'} arrow>
            <IconButton size="small" onClick={handleCopyMrn} aria-label="copy MRN">
              {mrnCopied
                ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                : <ContentCopyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              }
            </IconButton>
          </Tooltip>
        </Box>
      </Popover>

      {/* --- Delink Log Popover --- */}
      <DelinkLogPopover
        anchorEl={delinkLogAnchorEl}
        open={Boolean(delinkLogAnchorEl)}
        data={delinkLogData ?? null}
        onClose={handleCloseDelinkLog}
      />

      <ReceiptDialog
        open={receiptOpen}
        onClose={handleReceiptClose}
        invoiceId={receiptInvoiceId}
        billNo={receiptBillNo ?? undefined}
        data-testid="invoice-receipt-dialog"
      />
      <RefundDialog
        open={refundOpen}
        onClose={handleRefundClose}
        invoiceId={refundInvoiceId}
        patientId={refundPatientId}
        patientName={refundPatientName}
        onSuccess={handleRefundClose}
        data-testid="invoice-refund-dialog"
      />
      {paymentDetailsForModal && (
        <CollectPaymentModal
          open={collectPaymentOpen}
          onClose={handleCollectPaymentClose}
          onSave={handlePaymentSave}
          paymentDetails={paymentDetailsForModal}
          existingPaymentHistory={[]}
          walletBalance={0}
          walletAdvances={[]}
          paymentModes={paymentModes}
          upiProviders={upiProviders}
          showWallet={true}
          consumeFromPackage={consumeFromPackageForModal}
          packageBalance={packageBalanceForModal}
          packageName={packageNameForModal || undefined}
          hidePaymentModeToggle={consumeFromPackageForModal > 0 && consumeFromPackageForModal >= (paymentDetailsForModal?.netAmount ?? 0)}
          isSubmitting={createPaymentMutation.isPending}
          data-testid="invoice-collect-payment-modal"
        />
      )}
      <InvoiceReportGen
        open={invoiceReportOpen}
        onClose={handleInvoiceReportClose}
        data={invoiceReportDetails ?? null}
        isLoading={invoiceReportLoading}
        error={invoiceReportError?.message ?? null}
        data-testid="invoice-report-gen"
      />
      <PharmacyInvoiceReportGen
        open={pharmacyReportOpen}
        onClose={handlePharmacyReportClose}
        data={pharmacyReportDetails ?? null}
        isLoading={pharmacyReportLoading}
        error={pharmacyReportError?.message ?? null}
        transactions={pharmacyPaymentsData?.transactions ?? []}
        data-testid="pharmacy-invoice-report-gen"
      />

      {/* --- Package Advance Collection Modals --- */}
      <InsufficientAdvancePopup
        open={pkgAdvState.insufficientAdvanceOpen}
        onClose={pkgAdvActions.closeInsufficientAdvancePopup}
        onCollectAdvance={pkgAdvActions.handleOpenCollectPackageAdvance}
        title={pkgAdvState.insufficientAdvanceTitle || undefined}
        message={pkgAdvState.insufficientAdvanceMessage || undefined}
      />
      <CollectPackageAdvanceModal
        open={pkgAdvState.collectPackageAdvanceOpen}
        onClose={pkgAdvActions.closeCollectPackageAdvanceModal}
        onCollectPayment={pkgAdvActions.handlePackageAdvanceCollectPayment}
        packages={pkgAdvState.packageOptions}
      />
      {pkgAdvState.packageAdvancePaymentDetails && (
        <CollectPaymentModal
          open={pkgAdvState.collectPackagePaymentOpen}
          onClose={pkgAdvActions.closeCollectPackagePaymentModal}
          onSave={pkgAdvActions.handleSavePackageAdvance}
          paymentDetails={pkgAdvState.packageAdvancePaymentDetails}
          existingPaymentHistory={[]}
          walletBalance={0}
          walletAdvances={[]}
          paymentModes={paymentModes}
          upiProviders={upiProviders}
          showWallet={false}
          isSubmitting={pkgAdvState.isAdvanceSubmitting}
          data-testid="pkg-advance-collect-payment-modal"
        />
      )}

      {/* --- Delink Recipient Dialog --- */}
      <DelinkRecipientDialog
        open={delinkOpen}
        invoiceId={delinkInvoiceId}
        onClose={handleDelinkClose}
      />

      {/* --- Transfer Log Modal --- */}
      <TransferLogModal
        open={transferLogOpen}
        onClose={handleTransferLogClose}
        invoiceId={transferLogInvoiceId}
      />

      {/* --- Settle Bill Modal (Donor: Continue with Donor / Recipient) --- */}
      <SettleBillModal
        open={settleBillModalOpen}
        onClose={handleSettleBillModalClose}
        onContinueWithDonor={handleSettleContinueWithDonor}
        onContinueWithRecipient={handleSettleContinueWithRecipient}
      />

      {/* --- Recipient Selection Modal --- */}
      <RecipientSelectionModal
        open={recipientModalOpen}
        onClose={handleRecipientModalClose}
        onProceed={handleRecipientProceed}
        recipients={recipients}
        selectedRecipientId={selectedRecipientId}
        onSelectRecipient={setSelectedRecipientId}
        loading={recipientsLoading}
      />
    </Box>
  );
}

export default InvoiceTable;